// TySh (Type Tool Object Setting) additional-layer-info parser.
//
// Extracts text content from PSD type layers.  PSD type layers store their
// visible text in one of two places depending on Photoshop version:
//
//   Older Photoshop (pre-CS): TySh block is a proprietary binary blob.
//   Newer Photoshop (CS+):    TySh block starts with an OSType descriptor
//                              (version 1), followed by TextEngineData — a
//                              textual serialization that embeds the visible
//                              text as `/Editor << /Text (the text) >>`.
//
// Our own encoder (encodePsd.ts) writes a simplified OSType descriptor with
// a top-level "Txt " string key (not TextEngineData), so we must handle both.
//
// Strategy (in order):
//   1. Try to parse the block as a versioned OSType descriptor and look for a
//      top-level "Txt " string item — handles our own encoder's output and any
//      Photoshop file where the descriptor can be parsed cleanly.
//   2. Byte-scan the raw Uint8Array for the TextEngineData pattern
//      `/Text (...)` — finds the literal ASCII bytes for "/Text (", then
//      captures the raw bytes between that and the matching ")", then decodes
//      using BOM detection (UTF-16BE, UTF-16LE, or UTF-8).  This correctly
//      handles real Photoshop TySh blocks where the text is stored as
//      UTF-16BE with a 0xFE 0xFF BOM.
//   3. Fall back to empty string.
//
// The byte-scan approach is intentionally pragmatic: TextEngineData is a large
// serialized text blob appended after the descriptor header, and the
// `/Editor /Text (...)` construct is unique within it.  Matching the raw
// bytes is robust against descriptor-version variations and encoding
// differences.

import { BinaryReader } from "./BinaryReader";
import { readVersionedDescriptor, getItem, asString } from "./osTypeDescriptor";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface TyShResult {
  /** The visible text content, or empty string if extraction failed. */
  text: string;
  /** Resolved font family name from FontSet, or undefined if not found. */
  font?: string;
  /** Font size in points (same unit Photoshop stores; convert to px at render time). */
  fontSize?: number;
  /** Text fill colour in 0–255 integer channels. */
  color?: { r: number; g: number; b: number };
  /** Paragraph alignment derived from /Justification 0–6. */
  align?: "left" | "center" | "right" | "justify";
  /** Whether FauxBold is set on the first style run. */
  bold?: boolean;
  /** Whether FauxItalic is set on the first style run. */
  italic?: boolean;
  /** Whether Underline is set on the first style run. */
  underline?: boolean;
  /** Whether FontCaps is set (1=small caps, 2=all caps). Renderer uppercases text when true. */
  allCaps?: boolean;
}

/**
 * Parse a TySh/tySh additional-layer-info data block and extract the visible
 * text content.
 *
 * @param data - The raw bytes of the TySh block (without signature/key/length
 *   header — just the payload as stored in PsdAdditionalLayerInfo.data).
 */
export function parseTySh(data: Uint8Array): TyShResult {
  // Strategy 1: versioned OSType descriptor with a top-level "Txt " item.
  // This is what our own encoder writes and what some Photoshop versions use.
  try {
    const r = new BinaryReader(data);
    const { descriptor } = readVersionedDescriptor(r);
    const txtItem = getItem(descriptor, "Txt ");
    const text = asString(txtItem);
    if (text !== undefined && text.length > 0) {
      return { text };
    }
  } catch {
    // Descriptor parse failed — fall through to byte-scan strategy.
  }

  // Strategy 2: byte-pattern scan of the raw bytes for TextEngineData.
  // Real Photoshop TySh blocks contain a large TextEngineData blob (a textual
  // Adobe serialization) that includes:
  //   /Editor << /Text (the visible text) >>
  // The text inside the parens is stored as UTF-16BE with a BOM in real
  // Photoshop files, so we must operate on raw bytes and detect the encoding
  // from the BOM rather than decoding the whole block as UTF-8 first.
  const text = extractTextFromTextEngineData(data);
  if (text !== null) {
    // Style values in TextEngineData are always ASCII — decode the whole
    // block as UTF-8 to extract them with regex.  We explicitly do NOT apply
    // BOM detection here; that only applies to the /Text content itself.
    const ascii = new TextDecoder("utf-8").decode(data);
    const styles = extractStylesFromTextEngineData(ascii);
    return { text, ...styles };
  }

  return { text: "" };
}

// ---------------------------------------------------------------------------
// TextEngineData extraction (byte-pattern approach)
// ---------------------------------------------------------------------------

/**
 * Search for the ASCII byte sequence "/Text (" in the raw bytes, extract the
 * content bytes up to the matching ")" (handling backslash escapes and paren
 * nesting), then decode using BOM detection.
 *
 * Returns the decoded, unescaped text, or null if the pattern is not found.
 */
function extractTextFromTextEngineData(bytes: Uint8Array): string | null {
  // ASCII for "/Text ("  →  0x2F 0x54 0x65 0x78 0x74 0x20 0x28
  const needle = new Uint8Array([0x2f, 0x54, 0x65, 0x78, 0x74, 0x20, 0x28]);
  const startIdx = findBytes(bytes, needle);
  if (startIdx === -1) return null;
  const contentStart = startIdx + needle.length;

  // Find the matching closing ")" while respecting backslash escapes and
  // nested parens so that embedded \( and \) are handled correctly.
  let depth = 1;
  let i = contentStart;
  while (i < bytes.length && depth > 0) {
    const b = bytes[i];
    if (b === 0x5c) {
      // backslash — the next byte is escaped, skip it
      i += 2;
      continue;
    }
    if (b === 0x28) depth++; // '('
    if (b === 0x29) depth--; // ')'
    if (depth === 0) break;
    i++;
  }
  if (depth !== 0) return null;
  const contentEnd = i;

  const textBytes = bytes.slice(contentStart, contentEnd);
  return decodeWithBom(textBytes);
}

/**
 * Decode a byte array using BOM detection:
 *   0xFE 0xFF → UTF-16BE
 *   0xFF 0xFE → UTF-16LE
 *   0xEF 0xBB 0xBF → UTF-8 with BOM
 *   otherwise → UTF-8
 *
 * For the UTF-16 cases we MUST decode manually rather than via TextDecoder,
 * because Photoshop's TextEngineData escapes literal `(`, `)`, and `\` with a
 * backslash followed by the bare ASCII byte of the character — e.g. an escaped
 * "(" is stored as the BE unit 0x005C (backslash) followed by a SINGLE byte
 * 0x28, not the 2-byte unit 0x0028.  That odd extra byte breaks UTF-16 2-byte
 * alignment for everything after it, so TextDecoder produces byte-swapped CJK
 * mojibake.  Decoding unit-by-unit and consuming exactly one byte for the
 * escaped char keeps the stream aligned and unescapes in the same pass.
 *
 * For the UTF-8 path (our own encodePsd output) the escapes are plain
 * single-byte backslash sequences, handled by unescapePsText after decode.
 */
function decodeWithBom(bytes: Uint8Array): string {
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return decodeEscapedUtf16(bytes.subarray(2), "be");
  }
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return decodeEscapedUtf16(bytes.subarray(2), "le");
  }
  if (
    bytes.length >= 3 &&
    bytes[0] === 0xef &&
    bytes[1] === 0xbb &&
    bytes[2] === 0xbf
  ) {
    // UTF-8 BOM
    return unescapePsText(
      new TextDecoder("utf-8").decode(bytes.subarray(3)),
    );
  }
  // Default: UTF-8 (no BOM) — our own encodePsd uses this path
  return unescapePsText(new TextDecoder("utf-8").decode(bytes));
}


/**
 * Decode escape-aware UTF-16 (BE or LE) from Photoshop TextEngineData.
 *
 * Photoshop escapes bytes at the RAW BYTE LEVEL within the `/Text (...)` PS
 * string.  Any byte with value 0x5C (backslash) is stored as two bytes
 * `5C 5C`, and any `(` / `)` byte is stored as `5C 28` / `5C 29`.  This
 * byte-level escaping is independent of UTF-16 code unit boundaries.
 *
 * Two escape patterns arise in UTF-16BE text:
 *
 *   a) A LITERAL BACKSLASH (U+005C = `00 5C`) is stored as `00 5C 5C`
 *      (the `5C` low byte is escaped) — 3 bytes for one code unit.
 *      This is what commit 2aa812e fixed.
 *
 *   b) A CHARACTER WHOSE HIGH BYTE IS 0x5C (e.g. 小 = U+5C0F = `5C 0F`) is
 *      stored as `5C 5C 0F` (the `5C` high byte is escaped) — 3 bytes for
 *      one code unit.  The unit-level decoder missed this because it read
 *      `5C 5C` as the unit 0x5C5C, which is not 0x005C, so no escape branch
 *      was taken and the character was emitted as a wrong CJK codepoint.
 *
 * Fix: decode byte-by-byte.  Read the high and low bytes of each code unit
 * individually, unescaping each one as we go.  Then assemble the code unit
 * from the true (unescaped) high and low bytes.
 *
 * Plain CR / LF units (0x000D / 0x000A) are normalised to `\n`.
 */
function decodeEscapedUtf16(b: Uint8Array, endian: "be" | "le"): string {
  let out = "";
  let i = 0;

  /**
   * Read one byte from the byte-escaped stream at position `pos`.
   * If the byte at `pos` is 0x5C (backslash), the NEXT byte is the real
   * byte value and we advance by 2; otherwise we advance by 1.
   * Returns [realByte, newPosition].
   */
  function readEscapedByte(pos: number): [number, number] {
    const byte0 = b[pos]!;
    if (byte0 === 0x5c && pos + 1 < b.length) {
      // Escaped byte: the next byte is the actual value.
      return [b[pos + 1]!, pos + 2];
    }
    return [byte0, pos + 1];
  }

  while (i < b.length) {
    // Read the two bytes that form a UTF-16 code unit, unescaping each.
    let hiByte: number, loByte: number;
    [hiByte, i] = readEscapedByte(i);
    if (i >= b.length) break; // trailing odd byte (rare) — stop
    [loByte, i] = readEscapedByte(i);

    const hi = endian === "be" ? hiByte : loByte;
    const lo = endian === "be" ? loByte : hiByte;
    const unit = (hi << 8) | lo;

    if (unit === 0x000d || unit === 0x000a) {
      out += "\n"; // normalise CR / LF to newline
      continue;
    }
    out += String.fromCharCode(unit);
  }
  return out;
}

/**
 * Find the first occurrence of `needle` bytes inside `haystack`.
 * Returns the index of the first matching byte, or -1 if not found.
 */
function findBytes(haystack: Uint8Array, needle: Uint8Array): number {
  outer: for (let i = 0; i <= haystack.length - needle.length; i++) {
    for (let j = 0; j < needle.length; j++) {
      if (haystack[i + j] !== needle[j]) continue outer;
    }
    return i;
  }
  return -1;
}

// ---------------------------------------------------------------------------
// Style extraction from the ASCII portion of TextEngineData
// ---------------------------------------------------------------------------

/**
 * Extract style attributes from a UTF-8-decoded TextEngineData string.
 *
 * All style values (font names, numbers, booleans) are ASCII even in real
 * Photoshop files, so decoding the blob as UTF-8 is safe for this purpose.
 * The /Text content is decoded separately via BOM detection.
 *
 * Returns a partial TyShResult with whichever style fields were found.
 */
function extractStylesFromTextEngineData(
  ascii: string,
): Omit<TyShResult, "text"> {
  const result: Omit<TyShResult, "text"> = {};

  // -------------------------------------------------------------------------
  // FontSet: /FontSet [ << /Name (FontName) >> ... ]
  // Collect all /Name (…) entries in order — they are indexed by /Font N.
  // -------------------------------------------------------------------------
  const fontNames: string[] = [];
  // Find the /FontSet [ ... ] block to limit scope of /Name matches.
  const fontSetMatch = ascii.match(/\/FontSet\s*\[([\s\S]*?)\]/);
  if (fontSetMatch) {
    const fontSetBlock = fontSetMatch[1]!;
    const nameRe = /\/Name\s*\(([^)]*)\)/g;
    let nm: RegExpExecArray | null;
    while ((nm = nameRe.exec(fontSetBlock)) !== null) {
      fontNames.push(nm[1]!);
    }
  }

  // -------------------------------------------------------------------------
  // StyleRun: first /Font N, /FontSize N, /FillColor, /FauxBold, /FauxItalic,
  // /Underline
  //
  // We use the first occurrence of each key in the entire blob (which
  // corresponds to the first style run, typically covering the whole layer
  // for single-style text or the first run for multi-style text).
  // -------------------------------------------------------------------------

  // /Font N — font index into FontSet
  const fontIdxMatch = ascii.match(/\/Font\s+(\d+)/);
  if (fontIdxMatch) {
    const idx = parseInt(fontIdxMatch[1]!, 10);
    const resolved = (idx >= 0 && idx < fontNames.length)
      ? fontNames[idx]
      : fontNames[0]; // out of range — fall back to first
    if (resolved !== undefined) result.font = resolved;
  } else if (fontNames.length > 0) {
    // /Font not present but FontSet is — use first entry.
    const first = fontNames[0];
    if (first !== undefined) result.font = first;
  }

  // /FontSize N or N.N
  const fontSizeMatch = ascii.match(/\/FontSize\s+([\d.]+)/);
  if (fontSizeMatch) {
    const pts = parseFloat(fontSizeMatch[1]!);
    if (!isNaN(pts) && pts > 0) result.fontSize = pts;
  }

  // /FillColor << /Type 1 /Values [ alpha r g b ] >>
  // Values are normalized 0..1 floats.  Type 1 = RGBA (Photoshop internal).
  const fillColorMatch = ascii.match(
    /\/FillColor\s*<<[^>]*?\/Values\s*\[\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*\]/,
  );
  if (fillColorMatch) {
    // Format: alpha r g b (all 0..1).
    const r = Math.round(parseFloat(fillColorMatch[2]!) * 255);
    const g = Math.round(parseFloat(fillColorMatch[3]!) * 255);
    const b = Math.round(parseFloat(fillColorMatch[4]!) * 255);
    if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
      result.color = {
        r: Math.min(255, Math.max(0, r)),
        g: Math.min(255, Math.max(0, g)),
        b: Math.min(255, Math.max(0, b)),
      };
    }
  }

  // /Justification N  (0=left 1=right 2=center 3-6=justify variants)
  const justMatch = ascii.match(/\/Justification\s+(\d+)/);
  if (justMatch) {
    const j = parseInt(justMatch[1]!, 10);
    if (j === 1) result.align = "right";
    else if (j === 2) result.align = "center";
    else if (j >= 3) result.align = "justify";
    else result.align = "left";
  }

  // /FauxBold true|false
  const fauxBoldMatch = ascii.match(/\/FauxBold\s+(true|false)/);
  if (fauxBoldMatch) result.bold = fauxBoldMatch[1] === "true";

  // /FauxItalic true|false
  const fauxItalicMatch = ascii.match(/\/FauxItalic\s+(true|false)/);
  if (fauxItalicMatch) result.italic = fauxItalicMatch[1] === "true";

  // /Underline true|false
  const underlineMatch = ascii.match(/\/Underline\s+(true|false)/);
  if (underlineMatch) result.underline = underlineMatch[1] === "true";

  // /FontCaps N (0=normal, 1=small caps, 2=all caps). Common in serif headings.
  const fontCapsMatch = ascii.match(/\/FontCaps\s+(\d+)/);
  if (fontCapsMatch) {
    const caps = parseInt(fontCapsMatch[1]!, 10);
    if (caps === 2 || caps === 1) result.allCaps = true;
  }

  return result;
}

/**
 * Unescape Photoshop TextEngineData string escapes:
 *   \\  → \  (must be first to avoid double-processing)
 *   \r  → \n (line break)
 *   \n  → \n
 *   \t  → \t (tab)
 *   \(  → (
 *   \)  → )
 */
function unescapePsText(s: string): string {
  // Process using a single pass to avoid double-unescaping.
  return s.replace(/\\([\s\S])/g, (_, ch: string) => {
    switch (ch) {
      case "r": return "\n";
      case "n": return "\n";
      case "t": return "\t";
      case "(": return "(";
      case ")": return ")";
      case "\\": return "\\";
      default: return ch;
    }
  });
}
